// src/components/EmailComposer.js — review / edit an email before it goes out
// [marker: email-compose v1]
// <EmailComposer kind="invoice|ticket_confirmation|portal_invite" id={…}
//                title="…" onClose onSent />
// Pick any saved template, edit To/CC/subject/body, optionally save the result
// as a new template, then send. The org header/footer is added automatically.
import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../utils/api';
import toast from 'react-hot-toast';

const MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};
const FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'align', 'link', 'image'];

export default function EmailComposer({ kind, id, title = 'Review Email', onClose, onSent, sendLabel = 'Send Email', initialTemplate, reminder }) {
  // [marker: dashboard v1] initialTemplate: start from this template; reminder: log it as a payment reminder
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [edited, setEdited] = useState(false);
  const [saveAs, setSaveAs] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [codeView, setCodeView] = useState(false); // designed (table) emails stay intact in code view

  const render = (key) => {
    setLoading(true);
    return api.post('/email-compose/render', { kind, id, templateKey: key || undefined })
      .then(r => {
        setTo(t => t || r.data.to || '');
        setCc(c => c || r.data.cc || '');
        setSubject(r.data.subject || '');
        setHtml(r.data.html || '');
        setCodeView(/<table/i.test(r.data.html || ''));
        setTemplateKey(r.data.templateKey || '');
        setNote(r.data.note || null);
        setEdited(false);
        setError(null);
      })
      .catch(err => setError(err.response?.data?.error || 'Could not prepare the email.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/email-compose/templates').then(r => setTemplates(r.data || [])).catch(() => {});
    render(initialTemplate || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  const switchTemplate = (key) => {
    if (edited && !window.confirm('Switch template? Your edits to this email will be replaced.')) return;
    render(key);
  };

  const send = async () => {
    if (!to.trim()) return toast.error('Enter who this goes to.');
    if (!subject.trim()) return toast.error('Subject is required.');
    if (saveAs && !saveName.trim()) return toast.error('Name the new template (or untick "Save as template").');
    setSending(true);
    try {
      const r = await api.post('/email-compose/send', {
        kind, id, to: to.trim(), cc: cc.trim(), subject: subject.trim(), html, templateKey,
        saveAsTemplate: saveAs ? saveName.trim() : undefined,
        reminder: reminder ? true : undefined,
      });
      toast.success(r.data.savedTemplate ? `Sent — and saved as template "${r.data.savedTemplate.template_name}".` : 'Email sent.');
      onSent && onSent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1500 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error ? (
            <div style={{ padding: 16, background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 6, color: 'var(--danger)' }}>{error}</div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Start from template</label>
                <select className="form-select" value={templateKey} onChange={e => switchTemplate(e.target.value)} disabled={loading}>
                  {!templates.some(t => t.template_key === templateKey) && templateKey && <option value={templateKey}>{templateKey}</option>}
                  {templates.map(t => <option key={t.template_key} value={t.template_key}>{t.template_name}</option>)}
                </select>
                <p className="form-hint">Changes here only affect this one email unless you tick “Save as a new template” below.</p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">To *</label>
                  <input className="form-input" type="email" value={to} onChange={e => setTo(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">CC (optional)</label>
                  <input className="form-input" type="email" value={cc} onChange={e => setCc(e.target.value)} placeholder="Send a copy to a second person" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input className="form-input" value={subject} onChange={e => { setSubject(e.target.value); setEdited(true); }} disabled={loading} />
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label">Message *</label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                    if (codeView && /<table/i.test(html) && !window.confirm('The visual editor will simplify this design (tables and layout are lost). Switch anyway?')) return;
                    setCodeView(v => !v);
                  }}>{codeView ? '✎ Visual editor' : '</> HTML code'}</button>
                </div>
                {loading ? (
                  <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>
                ) : codeView ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <textarea className="form-textarea" value={html} onChange={e => { setHtml(e.target.value); setEdited(true); }}
                              style={{ fontFamily: 'monospace', fontSize: '0.78rem', minHeight: 300 }} spellCheck={false} />
                    <iframe title="Preview" srcDoc={html} sandbox="" style={{ width: '100%', minHeight: 300, border: '1px solid var(--gray-200)', borderRadius: 6, background: 'white' }} />
                  </div>
                ) : (
                  <div style={{ background: 'white', borderRadius: 6 }}>
                    <ReactQuill theme="snow" value={html} onChange={v => { setHtml(v); setEdited(true); }}
                                modules={MODULES} formats={FORMATS} style={{ minHeight: 260 }} />
                  </div>
                )}
                {note && <p className="form-hint">{note}</p>}
                <p className="form-hint">The NJJBA header and styling are added automatically when it’s sent.{kind === 'invoice' ? ' 📎 The invoice PDF is attached automatically.' : ''}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={saveAs} onChange={e => setSaveAs(e.target.checked)} />
                  Save as a new template
                </label>
                {saveAs && (
                  <input className="form-input" style={{ maxWidth: 280, height: 32 }} placeholder="Template name" value={saveName} onChange={e => setSaveName(e.target.value)} />
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={loading || sending || !!error}>
            {sending ? 'Sending…' : `✉ ${sendLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
